/**
 * English - `auth`: the screens before the dashboard (sign in, sign up, the
 * email and WhatsApp codes, password resets, invitations, the forced password
 * change) and the Security page inside it. Grouped by screen: `layout.*`,
 * `common.*`, `login.*`, `register.*`, `verify.*`, `code.*`, `forgot.*`,
 * `reset.*`, `change.*`, `strength.*`, `social.*`, `whatsapp.*`, `security.*`.
 * Conventions: see `common.ts`.
 */
const auth: Record<string, string> = {
  // >>> layout
  'layout.back_home': 'Back to home',
  'layout.strapline': 'AI Commerce OS for Indian businesses',

  // >>> common
  'common.email': 'Email address',
  'common.password': 'Password',
  'common.full_name': 'Full name',
  'common.email_required': 'Enter your email address.',
  'common.email_invalid': 'Enter a valid email address.',
  'common.name_required': 'Enter your name.',
  'common.generic_error': 'Something went wrong. Please try again.',
  'common.back_to_sign_in': 'Back to sign in',
  'common.sign_in': 'Sign in',
  'common.sending': 'Sending…',
  'common.saving': 'Saving…',
  'common.too_long': 'That is longer than we can store.',
  'common.min_length': 'Use at least {min} characters.',
  'common.min_length_hint': 'At least {min} characters.',
  'common.too_repetitive': 'Too repetitive - mix it up a little.',

  // >>> login
  'login.title': 'Welcome back',
  'login.subtitle': 'Sign in to manage your store.',
  'login.password_required': 'Enter your password.',
  'login.captcha_pending': 'Complete the security check, then sign in.',
  'login.unverified': 'Verify {email} before signing in.',
  'login.send_new_code': 'Send a new code',
  'login.forgot': 'Forgot password?',
  'login.submitting': 'Signing in…',
  'login.new_here': 'New to NeonCarts?',
  'login.create_account': 'Create an account',
  'login.password_changed_title': 'Password changed',
  'login.password_changed':
    'You were signed out on every device. Sign in again with your new password.',

  // >>> register
  'register.title': 'Start selling online',
  'register.intro_trial': 'Create your account, then start your {days}-day free trial.',
  'register.intro_paid': 'Create your account, then set up your plan.',
  'register.intro_free': 'Create your account. No card required.',
  'register.have_account': 'Already have an account?',
  'register.business_name': 'Business name',
  'register.business_name_info':
    'Your storefront address is made from this, so use the name you want shoppers to see.',
  'register.business_required': 'Enter your business name.',
  'register.rule_upper': 'Add an uppercase letter.',
  'register.rule_lower': 'Add a lowercase letter.',
  'register.rule_number': 'Add a number.',
  'register.rule_special': 'Add a special character, such as ! @ # or -.',
  'register.code_too_long': 'That is longer than any code we issue.',
  'register.code_label': 'Promo or partner code (optional)',
  'register.code_hint':
    'From a partner, influencer or offer. It applies whether you sign up with Google, WhatsApp or email.',
  'register.code_toggle': 'Have a promo or partner code?',
  'register.deals_link': 'See today’s deals',
  'register.code_checking': 'Checking that code…',
  'register.code_unknown': 'We do not recognise that code. You can still create your account without it.',
  'register.code_referred_by': '{partner} referred you.',
  'register.code_accepted': 'Code accepted.',
  'register.code_no_benefit':
    'No discount is attached to this one, but your referrer gets the credit for sending you.',
  'register.plan_picked': 'You picked {plan}',
  'register.plan_trial':
    '{days}-day free trial. Nothing is charged until it ends, and you can cancel before then.',
  'register.plan_free': 'Free to start. Upgrade whenever your store is ready.',
  'register.plan_paid': 'You set up payment right after creating your account.',
  'register.change_plan': 'Change plan',
  'register.policies_required': 'Please read and accept the policies to create your account.',
  'register.policies_updated': 'Our policies were just updated. Please review them and accept again.',
  'register.submitting': 'Creating account…',
  'register.submit': 'Create account',

  // >>> verify
  'verify.link_failed': 'This link could not be used.',
  'verify.send_failed': 'We could not send a code. Try again.',
  'verify.verifying': 'Verifying your email address…',
  'verify.verified_title': 'Email verified',
  'verify.verified_body': 'Your account is ready. Sign in to continue.',
  'verify.title_check': 'Check your email',
  'verify.title_verify': 'Verify your email',
  'verify.sent_signup':
    'We sent a 6-digit code to {email}. Enter it below to finish setting up your account.',
  'verify.sent_later': 'If {email} needs verifying, a 6-digit code is on its way.',
  'verify.wrong_address': 'Wrong address?',
  'verify.start_again': 'Start again',
  'verify.go_back': 'Go back',
  'verify.intro': 'Enter your email address and we will send a fresh 6-digit code.',
  'verify.submit': 'Send code',

  // >>> code
  'code.label': '6-digit code',
  'code.wrong_one': 'That code is not right. {count} try left.',
  'code.wrong_other': 'That code is not right. {count} tries left.',
  'code.resent': 'A new code is on its way to {email}.',
  'code.enter_digits': 'Enter the {length}-digit code.',
  'code.email_hint': 'It can take a minute to arrive. Check your spam folder too.',
  'code.verifying': 'Verifying…',
  'code.submit': 'Verify and continue',
  'code.not_received': 'Didn’t get it?',
  'code.resend_in': 'Resend in {seconds}s',
  'code.resend': 'Resend code',

  // >>> forgot
  'forgot.title': 'Reset your password',
  'forgot.sent':
    'If an account exists for that address, a reset link is on its way. The link expires in an hour.',
  'forgot.intro': 'Enter your email address and we will send you a link to choose a new password.',
  'forgot.submit': 'Send reset link',

  // >>> reset
  'reset.only_numbers': 'Use more than just numbers.',
  'reset.mismatch': 'Passwords do not match.',
  'reset.missing_token': 'This link is missing its token. Request a new one.',
  'reset.done_title': 'Password set',
  'reset.done_body': 'Every other session was signed out. Use your new password to continue.',
  'reset.title_invite': 'Set your password',
  'reset.title_reset': 'Choose a new password',
  'reset.intro_invite': 'Pick a password to activate your account.',
  'reset.intro_reset': 'Signing in elsewhere will require the new password.',
  'reset.invalid_link': 'This link is not valid. Request a new one.',
  'reset.new_password': 'New password',
  'reset.confirm_password': 'Confirm password',
  'reset.submit': 'Save password',

  // >>> change
  'change.title_forced': 'Choose your own password',
  'change.title': 'Change your password',
  'change.intro_forced':
    'The password you signed in with was set by someone else. Choose one only you know to carry on.',
  'change.intro': 'You will be signed out everywhere, including on this device.',
  'change.given_password': 'The password you were given',
  'change.current_password': 'Current password',
  'change.new_password': 'New password',
  'change.confirm_password': 'Confirm new password',
  'change.mismatch': 'These do not match.',
  'change.generic_error': 'Something went wrong. Try again.',
  'change.submit': 'Change password',
  'change.sign_out': 'Sign out',
  'change.cancel': 'Cancel',

  // >>> strength
  'strength.weak': 'Weak',
  'strength.medium': 'Medium',
  'strength.strong': 'Strong',
  'strength.very_strong': 'Very strong',
  'strength.sr_label': 'Password strength:',
  'strength.rule_length': '{min}+ characters',
  'strength.rule_upper': 'Uppercase',
  'strength.rule_lower': 'Lowercase',
  'strength.rule_number': 'Number',
  'strength.rule_special': 'Special character',
  'strength.done': '(done)',
  'strength.missing': '(missing)',

  // >>> social
  'social.load_failed': 'You are signed in, but we could not load your account. Please refresh the page.',
  'social.referral_fix': '{message} Remove or correct the referral code, then try again.',
  'social.google_failed': 'Google sign-in did not complete. Please try again.',
  'social.account_ready': 'Your account is ready',
  'social.signed_in_with': 'Signed in with {provider}',
  'social.setting_up': 'Setting up your store…',
  'social.to_dashboard': 'Taking you to your dashboard…',
  'social.sign_up_with': 'Sign up with {provider}',
  'social.continue_with': 'Continue with {provider}',
  'social.waiting': 'Waiting…',
  'social.signing_in': 'Signing in…',
  'social.or': 'or',
  'social.or_sign_up_email': 'or sign up with email',
  'social.or_email': 'or use your email',

  // >>> whatsapp
  'whatsapp.other_number': 'Use a different number',
  'whatsapp.all_options': 'All sign-in options',
  'whatsapp.phone_intro': 'We will send a one-time code to your WhatsApp.',
  'whatsapp.code_sent_to': 'Code sent to {number}',
  'whatsapp.number_required': 'Enter your WhatsApp number.',
  'whatsapp.captcha_first': 'Complete the security check first.',
  'whatsapp.captcha_resend': 'Complete the security check, then resend.',
  'whatsapp.number_label': 'WhatsApp number',
  'whatsapp.country_code': 'Country code',
  'whatsapp.sending_code': 'Sending code…',
  'whatsapp.send_code': 'Send code',
  'whatsapp.messaging_note': 'Standard WhatsApp messaging applies. We never message you without asking.',
  'whatsapp.code_hint': 'Open WhatsApp and look for a message from NeonCarts.',

  // >>> security
  'security.title': 'Security',
  'security.active_now': 'Active now',
  'security.minutes_ago': '{count} minutes ago',
  'security.hours_ago_one': '{count} hour ago',
  'security.hours_ago_other': '{count} hours ago',
  'security.days_ago_one': '{count} day ago',
  'security.days_ago_other': '{count} days ago',
  'security.this_device': 'This device',
  'security.unknown_address': 'Unknown address',
  'security.signed_in_until': 'Signed in until {date}',
  'security.hide_details': 'Hide details',
  'security.show_details': 'Show details',
  'security.sign_out': 'Sign out',
  'security.sign_out_device': 'Sign out this device',
  'security.device_signed_out': 'That device was signed out.',
  'security.nothing_else': 'There was nothing else signed in.',
  'security.signed_out_others_one': 'Signed out {count} other device.',
  'security.signed_out_others_other': 'Signed out {count} other devices.',
  'security.password_title': 'Password',
  'security.password_description': 'Changing it signs you out on every device, including this one.',
  'security.change_password': 'Change password',
  'security.devices_title': 'Signed-in devices',
  'security.devices_description':
    'Everywhere your account is currently signed in. If you do not recognise something here, sign it out and change your password.',
  'security.sign_out_others': 'Sign out everywhere else',
  'security.unreachable': 'Could not reach the server. Check your connection and try again.',
  'security.checking': 'Checking your devices…',
  'security.empty_title': 'Nothing is signed in',
  'security.empty_description': 'Your session has already ended. Sign in again to see your devices.',
  'security.revoke_note':
    'Signing out a device revokes it immediately. A page already open on that device may keep working for a few more minutes, until the short-lived token it is holding expires.',
  'security.confirm_current_title': 'Sign out of this device?',
  'security.confirm_other_title': 'Sign out this device?',
  'security.cancel': 'Cancel',
  'security.signing_out': 'Signing out…',
  'security.confirm_current_body':
    'This is the device you are using now. You will be returned to the sign-in page.',
  'security.confirm_other_body': '{device} will need to sign in again to reach your business.',
  'security.confirm_other_body_ip': '{device} at {ip} will need to sign in again to reach your business.',
  'security.confirm_others_title': 'Sign out everywhere else?',
  'security.sign_out_count': 'Sign out {count}',
  'security.confirm_others_body_one':
    '{count} other device will need to sign in again. This device stays signed in.',
  'security.confirm_others_body_other':
    '{count} other devices will need to sign in again. This device stays signed in.',
};

export default auth;
